// src/services/phone/SipService.js
import JsSIP from 'jssip'
import mitt from 'mitt'
import toneService from '@/services/phone/ToneService'

const emitter = mitt()

class SipService {
  constructor() {
    this.userAgent = null
    this.session = null

    this.remoteAudio = null
    this.localStream = null
    this.remoteStream = null

    this.config = null
    this.started = false

    this._reconnectTimer = null
    this._registerRetryTimer = null
    this._callStartAt = null

    this.selectedInputId = null
    this.selectedOutputId = null

    this._initPromise = null
    this._keepAliveTimer = null

    this._onlineHandler = null
    this._offlineHandler = null

    this._sessionEventHandlers = []
    this._boundPeerConnection = null
    this._boundTrackHandler = null

    this._registered = false
    this._connecting = false
    this._registering = false
    this._regFailCount = 0

    this._lastOutgoingTarget = null
    this._transportWrapped = false
  }

  on(eventName, handler) {
    emitter.on(eventName, handler)
    return () => emitter.off(eventName, handler)
  }

  _emit(eventName, payload) {
    emitter.emit(eventName, payload)
  }

  _log(...args) {
    console.log('[SipService]', ...args)
  }

  _syncUaState() {
    const ua = this.userAgent
    const connected = !!ua?.isConnected?.()
    const registered = !!ua?.isRegistered?.()

    this._registered = registered

    this._emit(
      'transport:state',
      connected ? 'connected' : (this._connecting ? 'connecting' : 'disconnected')
    )

    this._emit(
      'reg:state',
      registered ? 'registered' : (this._registering ? 'registering' : 'unregistered')
    )
  }

  _buildAudioConstraints() {
    const audio = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true
    }

    if (this.selectedInputId) {
      audio.deviceId = { ideal: this.selectedInputId }
    }

    return audio
  }

  _getPcConfig() {
    return {
      // No STUN — Asterisk on public IP handles NAT. Opus codec provides audio quality fix.
      iceServers: [],
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require'
    }
  }

  async unlockAudio() {
    this._log('unlockAudio start')

    try {
      Promise.resolve(toneService.unlock())
        .then(() => this._log('toneService.unlock resolved'))
        .catch((e) => this._log('toneService.unlock rejected', e?.message || e))
    } catch (e) {
      this._log('toneService.unlock threw', e?.message || e)
    }

    this._ensureRemoteAudio()

    try {
      this._safePlayRemoteAudio().catch(() => {})
    } catch (e) {}

    this._log('unlockAudio done')
  }

  _ensureRemoteAudio() {
    if (!this.remoteAudio) {
      this.remoteAudio = new Audio()
    }

    this.remoteAudio.autoplay = true
    this.remoteAudio.playsInline = true

    if (!this.remoteStream) {
      this.remoteStream = new MediaStream()
    }

    if (this.remoteAudio.srcObject !== this.remoteStream) {
      this.remoteAudio.srcObject = this.remoteStream
    }

    if (this.selectedOutputId && typeof this.remoteAudio.setSinkId === 'function') {
      this.remoteAudio.setSinkId(this.selectedOutputId).catch(() => {})
    }
  }

  async _safePlayRemoteAudio() {
    if (!this.remoteAudio) return
    try {
      await this.remoteAudio.play()
    } catch (e) {
      this._emit('media:playback-blocked', e?.message || String(e))
    }
  }

  _wrapTransportSend() {
    if (this._transportWrapped) return

    const socket = this.userAgent?._transport?.socket
    if (!socket || typeof socket.send !== 'function') return

    const originalSend = socket.send.bind(socket)
    socket.send = (data) => {
      try {
        this._log(
          'WS SEND',
          typeof data === 'string' ? data.slice(0, 2000) : data
        )
      } catch (e) {}
      return originalSend(data)
    }

    this._transportWrapped = true
    this._log('transport send wrapped')
  }

  async init(config) {
    this.config = config

    if (this._initPromise) return this._initPromise
    if (this.started) return

    this._initPromise = (async () => {
      this._ensureRemoteAudio()

      const socket = new JsSIP.WebSocketInterface(config.wss)

      const uaConfig = {
        sockets: [socket],
        uri: `sip:${config.username}@${config.domain}`,
        password: config.password,
        display_name: config.displayName || '',
        register: false,
        session_timers: false,
        connection_recovery_min_interval: 2,
        connection_recovery_max_interval: 30
      }

      this.userAgent = new JsSIP.UA(uaConfig)

      this._wireUaEvents()
      this._wireBrowserSignals()

      this.userAgent.start()
      this.started = true
      this._emit('ua:started')
      this._syncUaState()
    })()

    try {
      return await this._initPromise
    } catch (e) {
      this._initPromise = null
      throw e
    }
  }

  _wireUaEvents() {
    if (!this.userAgent) return

    this.userAgent.on('connecting', () => {
      this._connecting = true
      this._syncUaState()
      this._log('UA connecting')
    })

    this.userAgent.on('connected', () => {
      this._connecting = false
      this._clearReconnect()
      this._startKeepAlive()
      this._syncUaState()
      this._log('UA connected')
      this._wrapTransportSend()

      if (!this.userAgent.isRegistered() && !this._registering) {
        this._safeRegisterSoon()
      }
    })

    this.userAgent.on('disconnected', () => {
      this._connecting = false
      this._registered = false
      this._registering = false
      this._stopKeepAlive()
      this._syncUaState()
      this._log('UA disconnected')
      this._scheduleReconnect()
    })

    this.userAgent.on('registered', () => {
      this._registered = true
      this._registering = false
      this._regFailCount = 0
      this._startKeepAlive()
      this._syncUaState()
      this._log('UA registered')
      this._emit('reg:ok')
    })

    this.userAgent.on('unregistered', () => {
      this._registered = false
      this._registering = false
      this._stopKeepAlive()
      this._syncUaState()
      this._log('UA unregistered')
    })

    this.userAgent.on('registrationFailed', (e) => {
      this._registered = false
      this._registering = false
      this._regFailCount++
      const cause = e?.cause || 'Registration failed'
      this._stopKeepAlive()
      this._syncUaState()
      this._log('registration failed', cause, `(attempt ${this._regFailCount}/5)`)
      this._emit('reg:state', 'failed')
      this._emit('reg:error', cause)

      if (this._regFailCount >= 5) {
        this._log('registration failed 5 times, giving up')
        this._emit('reg:gave-up')
        return
      }

      this._safeRegisterSoon()
    })

    this.userAgent.on('registrationExpiring', () => {
      this._log('registration expiring')
      if (!this._registering) {
        this.register().catch(() => {})
      }
    })

    this.userAgent.on('newRTCSession', (e) => {
      const session = e?.session
      const originator = e?.originator

      if (!session) return

      this._log('newRTCSession', {
        originator,
        direction: session.direction,
        id: session.id
      })

      if (this.session && this.session !== session && !this.session.isEnded()) {
        this._log('rejecting second session while busy')
        try {
          session.terminate({
            status_code: 486,
            reason_phrase: 'Busy Here'
          })
        } catch (err) {}
        return
      }

      this.session = session
      this._callStartAt = null
      this._wireSession(session, originator)
    })

    this.userAgent.on('newMessage', () => {
      this._emit('notify:received')
    })
  }

  _wireBrowserSignals() {
    if (!this._onlineHandler) {
      this._onlineHandler = () => this._safeReconnectNow()
      window.addEventListener('online', this._onlineHandler)
    }

    if (!this._offlineHandler) {
      this._offlineHandler = () => this._emit('net:offline')
      window.addEventListener('offline', this._offlineHandler)
    }
  }

  async register() {
    if (!this.userAgent) throw new Error('UA not initialized')

    if (this.userAgent.isRegistered()) {
      this._registered = true
      this._registering = false
      this._syncUaState()
      return
    }

    if (this._registering) {
      this._syncUaState()
      return
    }

    try {
      this._registering = true
      this._emit('reg:state', 'registering')
      this.userAgent.register()
      this._syncUaState()
      this._log('register requested')
    } catch (e) {
      this._registering = false
      this._emit('reg:error', e?.message || String(e))
      this._safeRegisterSoon()
      throw e
    }
  }

  async unregister() {
    try {
      if (this.userAgent) {
        this.userAgent.unregister({ all: false })
      }
    } finally {
      this._registered = false
      this._registering = false
      this._stopKeepAlive()
      this._syncUaState()
    }
  }

  async destroy() {
    this._clearReconnect()
    this._clearRegisterRetry()
    this._stopKeepAlive()
    toneService.stopAllCallTones()

    try {
      await this.hangup({ silent: true })
    } catch (e) {}

    try {
      if (this.userAgent) {
        this.userAgent.unregister({ all: false })
      }
    } catch (e) {}

    try {
      if (this.userAgent) {
        this.userAgent.stop()
      }
    } catch (e) {}

    if (this._onlineHandler) {
      window.removeEventListener('online', this._onlineHandler)
      this._onlineHandler = null
    }

    if (this._offlineHandler) {
      window.removeEventListener('offline', this._offlineHandler)
      this._offlineHandler = null
    }

    this._detachSessionHandlers()

    this.userAgent = null
    this.session = null
    this.started = false
    this._registered = false
    this._connecting = false
    this._registering = false
    this._initPromise = null
    this._lastOutgoingTarget = null
    this._transportWrapped = false

    this._emit('ua:stopped')
  }

  _scheduleReconnect() {
    if (this._reconnectTimer) return
    this._emit('reconnect:scheduled')

    this._reconnectTimer = setTimeout(async () => {
      this._reconnectTimer = null
      await this._safeReconnectNow()
    }, 2500)
  }

  _clearReconnect() {
    if (this._reconnectTimer) clearTimeout(this._reconnectTimer)
    this._reconnectTimer = null
  }

  async _safeReconnectNow() {
    if (!navigator.onLine) return
    if (!this.userAgent) return
    if (this._connecting) return

    try {
      this._emit('reconnect:trying')
      this._log('reconnect trying')
      this.userAgent.start()
      this._syncUaState()
    } catch (e) {
      this._emit('reconnect:error', e?.message || String(e))
      this._log('reconnect error', e)
    }
  }

  _safeRegisterSoon() {
    if (this._registerRetryTimer) return

    this._registerRetryTimer = setTimeout(async () => {
      this._registerRetryTimer = null
      try {
        await this.register()
      } catch (e) {}
    }, 1200)
  }

  _clearRegisterRetry() {
    if (this._registerRetryTimer) clearTimeout(this._registerRetryTimer)
    this._registerRetryTimer = null
  }

  _startKeepAlive() {
    if (this._keepAliveTimer) return
    this._keepAliveTimer = setInterval(() => {
      this._sendOptionsPing().catch(() => {})
    }, 20000)
  }

  _stopKeepAlive() {
    if (this._keepAliveTimer) clearInterval(this._keepAliveTimer)
    this._keepAliveTimer = null
  }

  async _sendOptionsPing() {
    if (!this.userAgent) return
    if (!this.config?.domain) return
    if (!this._registered) return

    try {
      this.userAgent.sendOptions(`sip:${this.config.domain}`, {
        eventHandlers: {
          succeeded: () => this._emit('keepalive:ok'),
          failed: (e) => this._emit('keepalive:error', e?.cause || 'OPTIONS failed')
        }
      })
    } catch (e) {
      this._emit('keepalive:error', e?.message || String(e))
    }
  }

  _wireSession(session, originator) {
    this._detachSessionHandlers()
    this._ensureRemoteAudio()

    let pcStartAt = null

    const addHandler = (event, handler) => {
      session.on(event, handler)
      this._sessionEventHandlers.push({ event, handler })
    }

    addHandler('peerconnection', (e) => {
      const pc = e?.peerconnection || session.connection
      pcStartAt = Date.now()

      this._log('peerconnection ready', { ts: pcStartAt })
      this._bindPeerConnection(pc)

      if (pc) {
        this._log('peerconnection states', {
          signalingState: pc.signalingState,
          iceGatheringState: pc.iceGatheringState,
          iceConnectionState: pc.iceConnectionState
        })

        pc.addEventListener('icegatheringstatechange', () => {
          this._log('iceGatheringState', {
            state: pc.iceGatheringState,
            elapsedMs: pcStartAt ? Date.now() - pcStartAt : null
          })
        })

        pc.addEventListener('icecandidate', (event) => {
          this._log('icecandidate', {
            elapsedMs: pcStartAt ? Date.now() - pcStartAt : null,
            hasCandidate: !!event.candidate,
            candidate: event.candidate?.candidate || null
          })
        })

        pc.addEventListener('iceconnectionstatechange', () => {
          const state = pc.iceConnectionState
          this._log('iceConnectionState', {
            state,
            elapsedMs: pcStartAt ? Date.now() - pcStartAt : null
          })

          if (state === 'failed' || state === 'disconnected') {
            // Grace period in case ICE recovers (common for short network blips).
            // If still bad after 5s, force-terminate so call:ended fires and state
            // (UI + backend presence) gets cleaned up instead of orphaning.
            setTimeout(() => {
              const current = pc.iceConnectionState
              if (current !== 'failed' && current !== 'disconnected') return
              if (!this.session || this.session.isEnded()) return
              this._log('ICE stuck, forcing session terminate', { state: current })
              try { this.session.terminate() } catch (_) {}
            }, 5000)
          }
        })

        pc.addEventListener('connectionstatechange', () => {
          this._log('pc.connectionState', {
            state: pc.connectionState,
            elapsedMs: pcStartAt ? Date.now() - pcStartAt : null
          })
        })
      }
    })

    addHandler('connecting', (e) => {
      this._log('session connecting', {
        hasRequest: !!e?.request,
        method: e?.request?.method || null
      })
      this._emit('call:state', 'connecting')
    })

    addHandler('sending', (e) => {
      this._log('INVITE sending', {
        method: e?.request?.method || null,
        ruri: e?.request?.ruri?.toString?.() || null,
        elapsedMsFromPc: pcStartAt ? Date.now() - pcStartAt : null
      })
    })

    addHandler('progress', () => {
      this._log('session progress')
      this._emit('call:state', 'progress')
      this._emit('call:progress')
    })

    addHandler('accepted', () => {
      this._log('session accepted')
      toneService.stopAllCallTones()
      this._emit('call:state', 'accepted')
      this._attachRemoteAudioNow()
    })

    addHandler('confirmed', () => {
      this._log('session confirmed')
      this._callStartAt = Date.now()
      toneService.stopAllCallTones()
      this._emit('call:state', 'established')
      this._emit('call:established')
      this._attachRemoteAudioNow()
    })

    addHandler('getusermediafailed', (e) => {
      this._log('getusermediafailed', e)
    })

    addHandler('peerconnection:createofferfailed', (e) => {
      this._log('createofferfailed', e)
    })

    addHandler('peerconnection:setlocaldescriptionfailed', (e) => {
      this._log('setlocaldescriptionfailed', e)
    })

    addHandler('peerconnection:setremotedescriptionfailed', (e) => {
      this._log('setremotedescriptionfailed', e)
    })

    addHandler('ended', (e) => {
      this._log('session ended', {
        originator: e?.originator || null,
        cause: e?.cause || null
      })
      toneService.stopAllCallTones()
      this._emit('call:state', 'terminated')
      this._emit('call:ended', { cause: e?.cause || 'ended' })
      this._cleanupMedia()
      this.session = null
      this._callStartAt = null
      this._lastOutgoingTarget = null
    })

    addHandler('failed', (e) => {
      this._log('session failed', {
        originator: e?.originator || null,
        cause: e?.cause || null,
        hasMessage: !!e?.message
      })
      toneService.stopAllCallTones()
      this._emit('call:error', e?.cause || 'Call failed')
      this._emit('call:state', 'terminated')
      this._emit('call:ended', { cause: e?.cause || 'failed' })
      this._cleanupMedia()
      this.session = null
      this._callStartAt = null
      this._lastOutgoingTarget = null
    })

    addHandler('hold', () => {
      this._emit('call:hold', true)
    })

    addHandler('unhold', () => {
      this._emit('call:hold', false)
    })

    addHandler('muted', () => {
      this._emit('call:mute', true)
    })

    addHandler('unmuted', () => {
      this._emit('call:mute', false)
    })

    addHandler('newDTMF', (e) => {
      const tone = e?.dtmf?.tone
      if (tone) this._emit('call:dtmf', tone)
    })

    addHandler('refer', () => {
      this._emit('call:refer:received')
    })

    addHandler('sdp', () => {
      this._attachRemoteAudioNow()
    })

    const isIncoming = originator === 'remote' || session.direction === 'incoming'

    if (isIncoming) {
      const from = session.remote_identity?.uri?.user || ''
      const name = session.remote_identity?.display_name || ''
      this._log('incoming call', { from, name })
      this._emit('call:incoming', { from, name })
      toneService.startRingtone().catch(() => {})
    } else {
      const to = session.remote_identity?.uri?.user || this._lastOutgoingTarget || ''
      this._log('outgoing call session wired', { to })
      this._emit('call:outgoing', { to })
      this._emit('call:state', 'dialing')
      toneService.startRingback().catch(() => {})
    }

    if (session.connection) {
      this._bindPeerConnection(session.connection)
    }
  }

  _detachSessionHandlers() {
    if (this.session && this._sessionEventHandlers.length) {
      this._sessionEventHandlers.forEach(({ event, handler }) => {
        try {
          this.session.off(event, handler)
        } catch (e) {}
      })
    }

    this._sessionEventHandlers = []

    if (this._boundPeerConnection && this._boundTrackHandler) {
      try {
        this._boundPeerConnection.removeEventListener('track', this._boundTrackHandler)
      } catch (e) {}
    }

    this._boundPeerConnection = null
    this._boundTrackHandler = null
  }

  _bindPeerConnection(pc) {
    if (!pc) return

    if (this._boundPeerConnection === pc) {
      this._attachRemoteAudioNow()
      return
    }

    if (this._boundPeerConnection && this._boundTrackHandler) {
      try {
        this._boundPeerConnection.removeEventListener('track', this._boundTrackHandler)
      } catch (e) {}
    }

    this._boundPeerConnection = pc

    this._boundTrackHandler = (event) => {
      const track = event?.track
      if (!track || track.kind !== 'audio') return

      this._log('remote audio track received', { id: track.id, muted: track.muted })

      if (event.streams && event.streams[0]) {
        this.remoteStream = event.streams[0]
      } else {
        if (!this.remoteStream) this.remoteStream = new MediaStream()
        const exists = this.remoteStream.getAudioTracks().some((t) => t.id === track.id)
        if (!exists) this.remoteStream.addTrack(track)
      }

      this.remoteAudio.srcObject = this.remoteStream
      this._safePlayRemoteAudio().catch(() => {})
      toneService.stopAllCallTones()
      this._emit('call:remote-media')

      track.onunmute = () => {
        this._log('remote audio track unmuted', { id: track.id })
        this.remoteAudio.srcObject = this.remoteStream
        this._safePlayRemoteAudio().catch(() => {})
        toneService.stopAllCallTones()
        this._emit('call:remote-media')
      }
    }

    pc.addEventListener('track', this._boundTrackHandler)
    this._attachRemoteAudioNow()
  }

  _attachRemoteAudioNow() {
    this._ensureRemoteAudio()

    const pc = this.session?.connection || this._boundPeerConnection
    if (!pc) {
      this.remoteAudio.srcObject = this.remoteStream
      this._safePlayRemoteAudio().catch(() => {})
      return
    }

    if (!this.remoteStream) {
      this.remoteStream = new MediaStream()
    }

    try {
      pc.getReceivers().forEach((receiver) => {
        const track = receiver?.track
        if (!track || track.kind !== 'audio') return

        const exists = this.remoteStream.getAudioTracks().some((t) => t.id === track.id)
        if (!exists) this.remoteStream.addTrack(track)

        if (!track.onunmute) {
          track.onunmute = () => {
            this._log('remote receiver track unmuted', { id: track.id })
            this.remoteAudio.srcObject = this.remoteStream
            this._safePlayRemoteAudio().catch(() => {})
            toneService.stopAllCallTones()
            this._emit('call:remote-media')
          }
        }
      })
    } catch (e) {}

    this.remoteAudio.srcObject = this.remoteStream
    this._safePlayRemoteAudio().catch(() => {})
  }

  async _ensureLocalMedia() {
    if (this.localStream) return this.localStream

    const constraints = {
      audio: this._buildAudioConstraints(),
      video: false
    }

    this._log('requesting local media', constraints)
    this.localStream = await navigator.mediaDevices.getUserMedia(constraints)
    this._log('local media ready', {
      audioTracks: this.localStream?.getAudioTracks?.().length || 0
    })

    return this.localStream
  }

  _cleanupMedia() {
    this._detachSessionHandlers()

    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => t.stop())
      this.localStream = null
    }

    this.remoteStream = null

    if (this.remoteAudio) {
      this.remoteAudio.srcObject = null
    }
  }

  async call(number, opts = {}) {
    this._log('call() entered', { number, ts: Date.now() })

    if (!this.userAgent) throw new Error('UA not initialized')
    if (!number) throw new Error('Missing number')
    if (this.session && !this.session.isEnded()) throw new Error('Already in a call')
    if (!this.userAgent.isConnected()) throw new Error('Transport not connected')
    if (!this.userAgent.isRegistered()) throw new Error('SIP not registered')

    this.unlockAudio().catch(() => {})
    this._wrapTransportSend()

    const targetUri = JsSIP.URI.parse(`sip:${number}@${this.config.domain}`)
    if (!targetUri) throw new Error('Invalid target URI')

    let extraHeaders = []
    if (Array.isArray(opts.extraHeaders)) {
      extraHeaders = opts.extraHeaders
        .map((h) => String(h ?? '').trim())
        .filter(Boolean)
    }

    const prettyTo = number
    this._lastOutgoingTarget = prettyTo

    this._log('placing call', {
      target: targetUri.toString(),
      prettyTo,
      extraHeaders,
      connected: this.userAgent.isConnected(),
      registered: this.userAgent.isRegistered()
    })

    this._emit('call:outgoing', { to: prettyTo })
    this._emit('call:state', 'dialing')

    let mediaStream
    try {
      mediaStream = await this._ensureLocalMedia()
    } catch (e) {
      this._log('getUserMedia failed before placing call', { name: e?.name, message: e?.message })
      toneService.stopAllCallTones()
      this._emit('call:error', 'Microphone unavailable: ' + (e?.message || String(e)))
      this._emit('call:state', 'terminated')
      this._emit('call:ended', { cause: 'media-failed' })
      this._lastOutgoingTarget = null
      throw e
    }

    try {
      this.userAgent.call(targetUri, {
        mediaStream,
        mediaConstraints: {
          audio: this._buildAudioConstraints(),
          video: false
        },
        pcConfig: this._getPcConfig(),
        rtcOfferConstraints: {
          offerToReceiveAudio: true,
          offerToReceiveVideo: false
        },
        extraHeaders,
        eventHandlers: {
          progress: () => this._log('INVITE progress', prettyTo),
          failed: (e) => this._log('INVITE failed', e?.cause || e),
          confirmed: () => this._log('INVITE confirmed', prettyTo),
          ended: (e) => this._log('INVITE ended', e?.cause || e)
        }
      })
    } catch (e) {
      this._log('call failed before session setup completed', e)
      toneService.stopAllCallTones()
      this._emit('call:error', e?.message || String(e))
      this._emit('call:state', 'terminated')
      this._lastOutgoingTarget = null
      throw e
    }
  }

  async answer() {
    if (!this.session) throw new Error('No session')
    if (this.session.direction !== 'incoming') throw new Error('Not an incoming call')

    let mediaStream
    try {
      mediaStream = await this._ensureLocalMedia()
    } catch (e) {
      this._log('getUserMedia failed on answer', { name: e?.name, message: e?.message })
      toneService.stopAllCallTones()
      this._emit('call:error', 'Microphone unavailable: ' + (e?.message || String(e)))
      try { this.session.terminate() } catch (_) {}
      throw e
    }

    this.unlockAudio().catch(() => {})
    toneService.stopRingtone()

    try {
      this.session.answer({
        mediaStream,
        mediaConstraints: {
          audio: this._buildAudioConstraints(),
          video: false
        },
        pcConfig: this._getPcConfig()
      })
    } catch (e) {
      this._emit('call:error', e?.message || String(e))
      throw e
    }
  }

  async hangup({ silent = false } = {}) {
    const s = this.session
    if (!s) return

    try {
      s.terminate()
    } finally {
      toneService.stopAllCallTones()
      if (!silent) this._emit('call:ended')
      this._cleanupMedia()
      this.session = null
      this._callStartAt = null
      this._lastOutgoingTarget = null
    }
  }

  setMute(muted) {
    const s = this.session
    if (!s || s.isEnded()) return

    try {
      if (muted) {
        s.mute({ audio: true, video: false })
      } else {
        s.unmute({ audio: true, video: false })
      }

      this._emit('call:mute', !!muted)
    } catch (e) {
      this._emit('call:error', e?.message || String(e))
    }
  }

  async setHold(hold) {
    const s = this.session
    if (!s || !s.isEstablished()) return

    try {
      if (hold) {
        s.hold()
      } else {
        s.unhold()
      }
      this._emit('call:hold', !!hold)
    } catch (e) {
      this._emit('call:error', e?.message || String(e))
    }
  }

  sendDTMF(tone) {
    const s = this.session
    if (!s || !s.isEstablished()) return

    const t = String(tone ?? '').trim()
    if (!t) return

    try {
      s.sendDTMF(t)
      this._emit('call:dtmf', t)
      toneService.playDtmf(t).catch(() => {})
    } catch (e) {
      this._emit('call:error', e?.message || String(e))
    }
  }

  async transfer(targetNumber) {
    const s = this.session
    if (!s || !s.isEstablished()) throw new Error('No active call')

    const target = /^sip:/i.test(targetNumber)
      ? targetNumber
      : `sip:${targetNumber}@${this.config.domain}`

    try {
      s.refer(target)
      this._emit('call:transfer', { to: targetNumber })
    } catch (e) {
      this._emit('call:error', e?.message || String(e))
      throw e
    }
  }

  getCallDurationMs() {
    if (!this._callStartAt) return 0
    return Date.now() - this._callStartAt
  }

  async listAudioDevices() {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const inputs = devices.filter((d) => d.kind === 'audioinput')
    const outputs = devices.filter((d) => d.kind === 'audiooutput')
    return { inputs, outputs }
  }

  async setAudioDevices({ inputId, outputId }) {
    if (inputId !== undefined) this.selectedInputId = inputId
    if (outputId !== undefined) this.selectedOutputId = outputId

    if (this.remoteAudio && outputId && typeof this.remoteAudio.setSinkId === 'function') {
      await this.remoteAudio.setSinkId(outputId).catch(() => {})
    }

    await toneService.setOutputDevice(this.selectedOutputId).catch(() => {})

    if (this.localStream && inputId) {
      this.localStream.getTracks().forEach((t) => t.stop())
      this.localStream = null
    }

    this._emit('devices:changed', {
      inputId: this.selectedInputId,
      outputId: this.selectedOutputId
    })
  }
}

const sipService = new SipService()
export default sipService